import React, { useMemo, useState, useEffect } from "react";
import { ButtonControl } from "spark-core-dx/components";
import "./setEditor.scss"

/* ---------- Logic (plain JS) ---------- */
const PTEXT = { 0: "Item/Data", 1: "Operation", 2: "UX", 3: "API" };
const FLAG_SPECS = [
  ["CanView", "View"],
  ["CanEdit", "Edit"],
  ["CanCreate", "Create"],
  ["CanDelete", "Delete"],
  ["CanHardDelete", "HardDelete"],
  ["CanExecute", "Execute"],
  ["CanViewOnNav", "ViewOnNav"],
  ["CanAccess", "Access"],
];
const ENTRY_COMPARE_FIELDS = [...FLAG_SPECS.map(([key]) => key), "ApplyDependencies"];

const DISABLE_MAP = {
  CanView: "DisableView",
  CanEdit: "DisableEdit",
  CanCreate: "DisableCreate",
  CanDelete: "DisableDelete",
  CanHardDelete: "DisableHardDelete",
  CanExecute: "DisableExecute",
  CanViewOnNav: "DisableViewOnNav",
  CanAccess: "DisableAccess",
};

const KEY_PREFIX = "PermissionSet:";
const makeKey = (id) => (id ? `${KEY_PREFIX}${id}` : null);

function readFromSession(id) {
  const key = makeKey(id);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { Entries: parsed };
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
}
function writeEntriesToSession(id, entries) {
  const key = makeKey(id);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ Entries: entries }));
  } catch (e) {}
}
function clearSessionForId(id) {
  const key = makeKey(id);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch (e) {}
}

function isFlagDisabled(p, key) {
  const k = DISABLE_MAP[key];
  return !!(p && p[k]);
}
function seedFlagFromDefaults(p, key) {
  return isFlagDisabled(p, key) ? false : !!(p && p[key]);
}

function humanizeCamel(s) {
  if (!s) return "";
  return s
    .replace(/^Can/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .trim();
}
function classNames() {
  return Array.from(arguments).filter(Boolean).join(" ");
}
function safe(s) {
  return s ?? "";
}
function readObjectPropCI(obj, ...names) {
  if (!obj || typeof obj !== "object") return undefined;
  const lookup = new Map(Object.keys(obj).map((key) => [key.toLowerCase(), key]));
  for (const name of names) {
    const key = lookup.get(String(name).toLowerCase());
    if (key !== undefined) return obj[key];
  }
  return undefined;
}
function normalizeBool(value) {
  return value === true || value === 1 || String(value ?? "").trim().toLowerCase() === "true";
}
function normalizeEntryForEditor(entry) {
  if (!entry || typeof entry !== "object") return null;
  const permission = String(readObjectPropCI(entry, "Permission", "permission", "PermissionId", "permissionId") ?? "").trim();
  if (!permission) return null;

  const normalized = {
    ...entry,
    Permission: permission
  };

  for (const [fieldName] of FLAG_SPECS) {
    const value = readObjectPropCI(entry, fieldName);
    normalized[fieldName] = value === undefined ? null : value;
  }

  const applyDependencies = readObjectPropCI(entry, "ApplyDependencies");
  normalized.ApplyDependencies = applyDependencies === undefined || applyDependencies === null
    ? true
    : normalizeBool(applyDependencies);

  return normalized;
}
function normalizeEntriesForEditor(entryList) {
  return (Array.isArray(entryList) ? entryList : [])
    .map(normalizeEntryForEditor)
    .filter(Boolean);
}
function getEntriesFingerprint(entryList) {
  const uniqueEntries = normalizeEntriesForEditor(entryList)
    .map((entry) => {
      const normalized = {
        Permission: String(entry?.Permission ?? "").trim()
      };
      for (const [fieldName] of FLAG_SPECS) {
        normalized[fieldName] = normalizeBool(entry?.[fieldName]);
      }
      normalized.ApplyDependencies = normalizeBool(entry?.ApplyDependencies);
      return normalized;
    })
    .reduce((map, entry) => {
      if (!map.has(entry.Permission)) {
        map.set(entry.Permission, entry);
        return map;
      }

      const existing = map.get(entry.Permission);
      ENTRY_COMPARE_FIELDS.forEach((fieldName) => {
        existing[fieldName] = existing[fieldName] || entry[fieldName];
      });
      return map;
    }, new Map());

  return JSON.stringify(
    Array.from(uniqueEntries.values())
      .sort((left, right) => left.Permission.localeCompare(right.Permission))
  );
}

function SectionCard({ title, subtitle, right, children }) {
  return (
    <div className="ps-card">
      {(title || right || subtitle) && (
        <div className="ps-card-header">
          <div>
            {title && <div className="ps-card-title">{title}</div>}
            {subtitle && <div className="ps-card-subtitle">{subtitle}</div>}
          </div>
          {right && <div>{right}</div>}
        </div>
      )}
      <div className="ps-card-body">{children}</div>
    </div>
  );
}

/**
 * Props:
 * - permissionSet: { _id, Name?, Description?, Version? | VersionNumber? | VersionNo? | VersionId?, Entries[] }
 * - permissions:   Permission catalog array
 * - refresh?:      any value; when it changes, force a sessionStorage reload
 * - onSave(updatedPermissionSet): optional
 * - onChange(updatedPermissionSet): optional — fires whenever entries change, passing the full object
 */
export function PermissionSetEditor({
  permissionSet,
  permissions,
  refresh,
  onSave,
  onChange
}) {
  const permList = useMemo(() => permissions ?? [], [permissions]);
  const psId = permissionSet?._id;

  // Prefer sessionStorage on first render
  const initialEntries = (() => {
    const saved = readFromSession(psId);
    if (saved && Array.isArray(saved.Entries)) return normalizeEntriesForEditor(saved.Entries);
    return normalizeEntriesForEditor(permissionSet?.Entries);
  })();

  const [entries, setEntries] = useState(initialEntries);
  const [undoEntries, setUndoEntries] = useState(null); // last version for Undo (after Rollback)
  const [canUndo, setCanUndo] = useState(false);
  const [aiDraftApplied, setAiDraftApplied] = useState(false);

  const [search, setSearch] = useState("");
  const [availSearch, setAvailSearch] = useState("");
  const [leftSort, setLeftSort] = useState("perm"); // 'perm' | 'type'
  const [rightSort, setRightSort] = useState("perm"); // 'perm' | 'type'
  const baselineEntriesFingerprint = useMemo(
    () => getEntriesFingerprint(permissionSet?.Entries),
    [permissionSet?.Entries]
  );
  const currentEntriesFingerprint = useMemo(
    () => getEntriesFingerprint(entries),
    [entries]
  );
  const hasUnsavedChanges = aiDraftApplied || currentEntriesFingerprint !== baselineEntriesFingerprint;

  // Resolve a version value from likely property names
  const versionValue = (() => {
    const v =
      permissionSet?.Version ??
      permissionSet?.VersionNumber ??
      permissionSet?.VersionNo ??
      permissionSet?.VersionId;
    return v === undefined || v === null || v === "" ? null : String(v);
  })();

  // Emit onChange with full object whenever entries change
  useEffect(() => {
    if (typeof onChange === "function") {
      try {
        onChange({ ...(permissionSet ?? {}), Entries: entries });
      } catch (e) {}
    }
  }, [entries, permissionSet, onChange]);

  // Re-load from session when _id, inbound object, or refresh changes
  useEffect(() => {
    const saved = readFromSession(psId);
    const inbound = normalizeEntriesForEditor(permissionSet?.Entries);
    const inboundFingerprint = getEntriesFingerprint(inbound);
    if (saved && Array.isArray(saved.Entries)) {
      const savedEntries = normalizeEntriesForEditor(saved.Entries);
      if (getEntriesFingerprint(savedEntries) === inboundFingerprint) {
        clearSessionForId(psId);
        setEntries(inbound);
      } else {
        setEntries(savedEntries);
      }
    } else {
      setEntries(inbound);
      if (psId) writeEntriesToSession(psId, inbound); // seed if empty
    }
    // any context change invalidates undo buffer
    setUndoEntries(null);
    setCanUndo(false);
    setAiDraftApplied(false);
  }, [psId, permissionSet]);

  useEffect(()=>{
    if (refresh && Array.isArray(refresh.Entries)) {
      const inbound = normalizeEntriesForEditor(refresh?.Entries);
      setEntries(inbound);
      writeEntriesToSession(refresh._id ?? psId, inbound);
      if (refresh.__aiAcceptedDraft) {
        setAiDraftApplied(true);
      }
    }
  },[psId, refresh])

  const permMap = useMemo(() => new Map(permList.map((p) => [p.Permission, p])), [permList]);

  const sortedEntries = useMemo(() => {
    const list = [...entries];
    return list.sort((a, b) => {
      const pa = permMap.get(a.Permission);
      const pb = permMap.get(b.Permission);
      const la = safe(pa?.DisplayName ?? a.Permission);
      const lb = safe(pb?.DisplayName ?? b.Permission);
      if (leftSort === "type") {
        const ta = pa?.PermissionType ?? 0;
        const tb = pb?.PermissionType ?? 0;
        if (ta !== tb) return ta - tb;
      }
      return la.localeCompare(lb);
    });
  }, [entries, permMap, leftSort]);

  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedEntries;
    return sortedEntries.filter((e) => {
      const p = permMap.get(e.Permission);
      const hay = safe(p?.DisplayName).toLowerCase();
      return hay.includes(q);
    });
  }, [sortedEntries, search, permMap]);

  const used = useMemo(() => new Set(entries.map((e) => e.Permission)), [entries]);

  const availablePerms = useMemo(() => {
    const q = availSearch.trim().toLowerCase();
    const list = permList.filter((p) => !used.has(p.Permission));
    const filtered = q ? list.filter((p) => safe(p.DisplayName).toLowerCase().includes(q)) : list;
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (rightSort === "type") {
        const ta = a?.PermissionType ?? 0;
        const tb = b?.PermissionType ?? 0;
        if (ta !== tb) return ta - tb;
      }
      const la = safe(a.DisplayName ?? a.Permission);
      const lb = safe(b.DisplayName ?? b.Permission);
      return la.localeCompare(lb);
    });
    return arr;
  }, [permList, availSearch, used, rightSort]);

  /* ---------- Undo buffer helpers ---------- */
  const clearUndo = () => {
    setUndoEntries(null);
    setCanUndo(false);
  };
  const invalidateUndoOnEdit = () => {
    clearUndo();
  };

  /* ---------- Mutations: update state AND write to session immediately ---------- */
  function addPermission(permId) {
    if (!permId || used.has(permId)) return;
    invalidateUndoOnEdit();
    const p = permMap.get(permId) || {};
    setEntries((prev) => {
      const next = [
        ...prev,
        {
          Permission: permId,
          CanView: seedFlagFromDefaults(p, "CanView"),
          CanEdit: seedFlagFromDefaults(p, "CanEdit"),
          CanCreate: seedFlagFromDefaults(p, "CanCreate"),
          CanDelete: seedFlagFromDefaults(p, "CanDelete"),
          CanHardDelete: seedFlagFromDefaults(p, "CanHardDelete"),
          CanExecute: seedFlagFromDefaults(p, "CanExecute"),
          CanViewOnNav: seedFlagFromDefaults(p, "CanViewOnNav"),
          CanAccess: seedFlagFromDefaults(p, "CanAccess"),
          ApplyDependencies: true,
        },
      ];
      writeEntriesToSession(psId, next);
      return next;
    });
  }

  function removePermission(permId) {
    invalidateUndoOnEdit();
    setEntries((prev) => {
      const next = prev.filter((e) => e.Permission !== permId);
      writeEntriesToSession(psId, next);
      return next;
    });
  }

  function setFlag(permId, key, value) {
    const p = permMap.get(permId);
    if (isFlagDisabled(p, key)) value = false;
    invalidateUndoOnEdit();
    setEntries((prev) => {
      const next = prev.map((e) => (e.Permission === permId ? { ...e, [key]: value } : e));
      writeEntriesToSession(psId, next);
      return next;
    });
  }

  /* ---------- Save / Rollback-Undo / Clear ---------- */
  const handleSave = async () => {
    if (!hasUnsavedChanges) {
      return { Success: false, NoChanges: true };
    }

    const updated = { ...(permissionSet ?? {}), Entries: entries };
    if (onSave) {
      const result = await onSave(updated);
      if (result?.Success) {
        clearSessionForId(psId);
        setUndoEntries(null);
        setCanUndo(false);
        setAiDraftApplied(false);
      }
      return result;
    }
  };

  // Rollback toggles to Undo: store current into undo buffer, then reset to inbound/server state.
  const handleRevertOrUndo = () => {
    if (canUndo && undoEntries) {
      setEntries(undoEntries);
      writeEntriesToSession(psId, undoEntries);
      clearUndo();
    } else {
      setUndoEntries(entries);
      setCanUndo(true);

      clearSessionForId(psId);
      const inbound = permissionSet?.Entries ?? [];
      setEntries(inbound);
      writeEntriesToSession(psId, inbound);
      setAiDraftApplied(false);
    }
  };

  // Hard Clear: wipe session buffer and in-memory entries; no undo.
  const handleHardClear = () => {
    clearSessionForId(psId);
    setEntries([]);
    setUndoEntries(null);
    setCanUndo(false);
    setAiDraftApplied(false);
  };

  function EntryRow({ e }) {
    const p = permMap.get(e.Permission);
    const display = p?.DisplayName ?? e.Permission;
    const t = PTEXT[p?.PermissionType ?? 0];

    return (
      <div className="ps-card ps-entry-card">
        <div className="ps-entry-header">
          <div className="ps-entry-left">
            <div className="ps-entry-top">
              <div className="ps-name" title={display}>
                {display}
              </div>
              <span className="ps-badge">{t}</span>
            </div>
            {p?.Description && (
              <div className="ps-desc-line" title={p.Description}>
                {p.Description}
              </div>
            )}
          </div>
          <div className="ps-entry-right">
            <button
              type="button"
              onClick={() => removePermission(e.Permission)}
              className="ps-btn"
              title="Remove from set"
            >
              Remove
            </button>
            <label className="ps-checkbox-row" title="Apply Permission Dependencies when set is applied">
              <input
                type="checkbox"
                checked={!!e.ApplyDependencies}
                onChange={(ev) => setFlag(e.Permission, "ApplyDependencies", ev.target.checked)}
              />
              <span>Apply dependencies</span>
            </label>
          </div>
        </div>

        <div className="ps-flags-wrap">
          <div className="ps-flags-scroller">
            <div className="ps-flags">
              {FLAG_SPECS.map(([key, label]) => {
                const dis = isFlagDisabled(p, key);
                const checked = dis ? false : !!e[key];
                return (
                  <label key={key} className={classNames("ps-flag", dis && "disabled")}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={dis}
                      onChange={(ev) => setFlag(e.Permission, key, ev.target.checked)}
                    />
                    <span>{humanizeCamel(label)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ps-root">
      {/* Header (Save + Rollback/Undo + Clear) */}
      <div className="ps-header" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div className="ps-header-left">
          <div className="ps-title">
            {permissionSet?.Name}
            {versionValue ? ` (Version ${versionValue})` : ""}
          </div>
          {permissionSet?.Description && <div className="ps-desc">{permissionSet.Description}</div>}
        </div>

        <div className="ps-actions" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <ButtonControl
            type="standard"
            onClick={handleRevertOrUndo}
            title={
              canUndo
                ? "Undo last Rollback (restore the previous working copy)"
                : "Rollback to the server state (you can Undo this)"
            }
          >
            {canUndo ? "Undo Rollback" : "Rollback"}
          </ButtonControl>

          <ButtonControl
            type="cancel"
            onClick={handleHardClear}
            title="Clear everything: wipe session buffer and empty the current entries"
          >
            Clear
          </ButtonControl>

          <ButtonControl
            type="ok"
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            disabledTitle="No permission changes to save"
          >
            Save
          </ButtonControl>
        </div>
      </div>

      {/* Two-pane layout */}
      <div className="ps-grid">
        {/* Left: Current Entries */}
        <div className="ps-card-left">
          <SectionCard
            title={`Current Entries (${entries.length})`}
            subtitle="Sorted by Permission."
            right={
              <div className="ps-controls">
                <select
                  value={leftSort}
                  onChange={(e) => setLeftSort(e.target.value === "type" ? "type" : "perm")}
                  className="ps-select"
                >
                  <option value="perm">Sort: Permission</option>
                  <option value="type">Sort: Type</option>
                </select>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search permission set..."
                  className="ps-input"
                />
              </div>
            }
          >
            {filteredEntries.length === 0 ? (
              <div className="ps-empty">No entries match your search.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {filteredEntries.map((e) => (
                  <EntryRow key={e.Permission} e={e} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* Right: Available Permissions (with count) */}
        <div className="ps-card-right">
          <SectionCard
            title={`Available Permissions (${availablePerms.length})`}
            subtitle="Add permissions to this set. Defaults are applied on add."
            right={
              <div className="ps-controls">
                <select
                  value={rightSort}
                  onChange={(e) => setRightSort(e.target.value === "type" ? "type" : "perm")}
                  className="ps-select"
                >
                  <option value="perm">Sort: Permission</option>
                  <option value="type">Sort: Type</option>
                </select>
                <input
                  type="text"
                  value={availSearch}
                  onChange={(e) => setAvailSearch(e.target.value)}
                  placeholder="Search available..."
                  className="ps-input"
                />
              </div>
            }
          >
            {availablePerms.length === 0 ? (
              <div className="ps-empty">All permissions are already in the set or none match the search.</div>
            ) : (
              <div className="ps-available-scroll">
                <ul className="ps-list">
                  {availablePerms.map((p) => (
                    <li key={p.Permission} className="ps-item">
                      <div className="ps-item-left">
                        <div className="ps-item-top">
                          <div className="ps-dname" title={p.DisplayName ?? p.Permission}>
                            {p.DisplayName ?? p.Permission}
                          </div>
                          <span className="ps-badge">{PTEXT[p.PermissionType ?? 0]}</span>
                        </div>
                        {p.Description && (
                          <div className="ps-item-desc" title={p.Description}>
                            {p.Description}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => addPermission(p.Permission)}
                        className="ps-item-add"
                        title="Add to set"
                      >
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

export default PermissionSetEditor;
